import { showUserAlert } from "../utils/userErrorMessage";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Header from "../Component/Header";
import { supabase } from "../supabase/client";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";

const parseNumber = (value) => {
  const cleaned = String(value ?? "").replace(/[^\d.-]/g, "");
  if (!cleaned || cleaned === "-") return 0;
  const number = Number(cleaned);
  return Number.isFinite(number) ? number : 0;
};

const parseNumberOrNull = (value) => {
  const cleaned = String(value ?? "").replace(/[^\d.-]/g, "");
  if (!cleaned || cleaned === "-") return null;
  const number = Number(cleaned);
  return Number.isFinite(number) ? number : null;
};

const truncateNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? Math.trunc(number) : 0;
};

const truncateNumberOrNull = (value) => {
  const number = parseNumberOrNull(value);
  return number === null ? null : Math.trunc(number);
};

const scheduleOverrideFields = ["payment", "principal", "interest", "stampDuty", "balance"];

const normalizeScheduleOverrides = (value) => {
  if (!value || typeof value !== "object") return {};
  if (Array.isArray(value)) {
    return value.reduce((acc, item) => {
      const round = Number(item?.round);
      if (!Number.isFinite(round)) return acc;
      acc[round] = scheduleOverrideFields.reduce((fieldAcc, field) => {
        if (item[field] !== undefined && item[field] !== null && String(item[field]).trim() !== "") {
          fieldAcc[field] = String(item[field]);
        }
        return fieldAcc;
      }, {});
      return acc;
    }, {});
  }

  return Object.entries(value).reduce((acc, [roundKey, fields]) => {
    const round = Number(roundKey);
    if (!Number.isFinite(round) || !fields || typeof fields !== "object") return acc;
    acc[round] = scheduleOverrideFields.reduce((fieldAcc, field) => {
      if (fields[field] !== undefined && fields[field] !== null && String(fields[field]).trim() !== "") {
        fieldAcc[field] = String(fields[field]);
      }
      return fieldAcc;
    }, {});
    return acc;
  }, {});
};

const serializeScheduleOverrides = (overrides) =>
  Object.entries(overrides).reduce((acc, [round, fields]) => {
    const nextFields = scheduleOverrideFields.reduce((fieldAcc, field) => {
      const value = truncateNumberOrNull(fields?.[field]);
      if (value !== null) fieldAcc[field] = value;
      return fieldAcc;
    }, {});
    if (Object.keys(nextFields).length > 0) acc[round] = nextFields;
    return acc;
  }, {});

const applyScheduleOverrides = (rows, overrides, fallbackPrincipal) => {
  let previousBalance = parseNumber(fallbackPrincipal);
  const mergedRows = rows.map((row) => {
    const rowOverrides = overrides[row.round] || {};
    const merged = {
      ...row,
      payment:
        rowOverrides.payment !== undefined ? parseNumber(rowOverrides.payment) : truncateNumber(parseNumber(row.payment)),
      principal:
        rowOverrides.principal !== undefined
          ? parseNumber(rowOverrides.principal)
          : truncateNumber(parseNumber(row.principal)),
      interest:
        rowOverrides.interest !== undefined
          ? parseNumber(rowOverrides.interest)
          : truncateNumber(parseNumber(row.interest)),
      stampDuty:
        rowOverrides.stampDuty !== undefined
          ? parseNumber(rowOverrides.stampDuty)
          : truncateNumber(parseNumber(row.stampDuty)),
    };

    if (rowOverrides.balance !== undefined) {
      merged.balance = parseNumber(rowOverrides.balance);
    } else if (row.round === 0) {
      merged.balance = truncateNumber(parseNumber(row.balance));
    } else {
      merged.balance = Math.max(0, truncateNumber(previousBalance - merged.principal));
    }

    previousBalance = merged.balance;
    return merged;
  });

  const totalPayment = mergedRows.reduce((sum, row) => sum + parseNumber(row.payment), 0);
  const totalInterest = mergedRows.reduce((sum, row) => sum + parseNumber(row.interest), 0);
  const paymentRowCount = mergedRows.filter((row) => row.round > 0).length;

  return {
    rows: mergedRows,
    totalPayment,
    totalInterest,
    monthlyAverage: paymentRowCount ? totalPayment / paymentRowCount : 0,
  };
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const openPrintWindow = (title, bodyHtml) => {
  const printWindow = window.open("", "_blank", "width=1000,height=800");
  if (!printWindow) {
    showUserAlert("팝업이 차단되어 인쇄 화면을 열 수 없습니다.");
    return;
  }

  printWindow.document.write(`
    <!doctype html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>${escapeHtml(title)}</title>
        <style>
          @page { size: portrait; margin: 10mm; }
          body { font-family: Arial, "Malgun Gothic", sans-serif; color: #111827; }
          h1 { font-size: 18px; margin: 0 0 10px; }
          .meta { display: grid; grid-template-columns: repeat(5, 1fr); gap: 6px; margin-bottom: 10px; font-size: 11px; }
          .box { border: 1px solid #d1d5db; padding: 5px 6px; }
          .label { color: #4b5563; margin-bottom: 2px; }
          .value { text-align: right; font-weight: 700; }
          table { width: 100%; border-collapse: collapse; table-layout: fixed; font-size: 11px; }
          th, td { border: 1px solid #9ca3af; padding: 4px 5px; vertical-align: middle; }
          th { background: #e5efff; text-align: center; font-weight: 700; }
          td.center { text-align: center; }
          td.right { text-align: right; }
        </style>
      </head>
      <body>${bodyHtml}</body>
    </html>
  `);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => printWindow.print(), 250);
};

const formatMoney = (value) => truncateNumber(parseNumber(value)).toLocaleString();

const formatMoneyInput = (value) => {
  const number = parseNumber(value);
  if (!number && !String(value ?? "").trim()) return "";
  return truncateNumber(number).toLocaleString();
};

const buildEqualPayment = (principalAmount, monthlyRate, months) => {
  if (months <= 0 || principalAmount <= 0) return 0;
  if (monthlyRate === 0) return principalAmount / months;
  return (principalAmount * monthlyRate * (1 + monthlyRate) ** months) /
    ((1 + monthlyRate) ** months - 1);
};

const getRemainingBalanceForFixedPayment = (payment, monthlyRate, months) => {
  if (payment <= 0 || months <= 0) return 0;
  if (monthlyRate === 0) return payment * months;
  return (payment * ((1 + monthlyRate) ** months - 1)) /
    (monthlyRate * (1 + monthlyRate) ** months);
};

const toUtcDate = (dateText) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(dateText || ""))) return null;
  const [year, month, day] = dateText.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day));
};

const formatDate = (date) => {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getLastDayOfMonth = (year, month) => new Date(Date.UTC(year, month, 0)).getUTCDate();

const resolveFirstRepaymentDate = (loanStartDate, maturityDate) => {
  const startDate = toUtcDate(loanStartDate);
  if (!startDate || !/^\d{4}-\d{2}-\d{2}$/.test(String(maturityDate || ""))) return null;

  const repaymentDay = Number(String(maturityDate).slice(-2));
  if (!Number.isFinite(repaymentDay) || repaymentDay <= 0) return null;

  let year = startDate.getUTCFullYear();
  let month = startDate.getUTCMonth() + 1;
  let day = Math.min(repaymentDay, getLastDayOfMonth(year, month));
  let candidate = new Date(Date.UTC(year, month - 1, day));

  if (candidate < startDate) {
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
    day = Math.min(repaymentDay, getLastDayOfMonth(year, month));
    candidate = new Date(Date.UTC(year, month - 1, day));
  }

  return formatDate(candidate);
};

const getInclusiveDayCount = (fromDate, toDate) => {
  const from = toUtcDate(fromDate);
  const to = toUtcDate(toDate);
  if (!from || !to || to < from) return null;
  return Math.floor((to.getTime() - from.getTime()) / 86400000);
};

const calculateProratedInterest = ({ principalAmount, annualRate, loanStartDate, maturityDate }) => {
  const firstRepaymentDate = resolveFirstRepaymentDate(loanStartDate, maturityDate);
  const dayCount = getInclusiveDayCount(loanStartDate, firstRepaymentDate);
  if (!principalAmount || !annualRate || !dayCount) return null;
  const monthlyInterest = principalAmount * (annualRate / 100 / 12);
  return truncateNumber(monthlyInterest * (dayCount / 30));
};

const buildSchedule = ({
  principal,
  annualRate,
  termMonths,
  graceMonths,
  loanStartDate,
  maturityDate,
  firstPaymentAmount,
  firstPaymentInterest,
  stampDuty,
}) => {
  const principalAmount = parseNumber(principal);
  const annualRateNumber = parseNumber(annualRate);
  const monthlyRate = annualRateNumber / 100 / 12;
  const totalMonths = Math.max(0, Math.floor(parseNumber(termMonths)));
  const grace = Math.min(Math.max(0, Math.floor(parseNumber(graceMonths))), totalMonths);
  const repaymentMonths = Math.max(0, totalMonths - grace);
  const firstPayment = parseNumber(firstPaymentAmount);
  const firstInterestText = String(firstPaymentInterest ?? "").trim();
  const configuredFirstInterest = parseNumber(firstPaymentInterest);
  const firstStampDuty = parseNumber(stampDuty);
  const hasFirstPayment = String(firstPaymentAmount ?? "").trim() !== "" && firstPayment > 0;
  const rows = [
    {
      round: 0,
      payment: 0,
      principal: 0,
      interest: 0,
      stampDuty: 0,
      balance: principalAmount,
    },
  ];

  if (!principalAmount || !totalMonths) {
    return { rows, totalPayment: 0, totalInterest: 0, monthlyAverage: 0 };
  }

  let balance = principalAmount;
  let totalPayment = 0;
  let totalInterest = 0;
  let monthlyPayment = buildEqualPayment(principalAmount, monthlyRate, repaymentMonths);

  for (let round = 1; round <= totalMonths; round += 1) {
    let interest = balance * monthlyRate;
    let principalPayment = 0;
    let payment = interest;
    let roundStampDuty = 0;

    if (round > grace) {
      const isFirstRepaymentRound = round === grace + 1;
      if (isFirstRepaymentRound && hasFirstPayment) {
        payment = firstPayment;
        const remainingRepaymentMonths = Math.max(0, repaymentMonths - 1);
        roundStampDuty = firstStampDuty;
        const proratedInterest = firstInterestText
          ? null
          : calculateProratedInterest({
              principalAmount: balance,
              annualRate: annualRateNumber,
              loanStartDate,
              maturityDate,
            });

        if (firstInterestText) {
          interest = configuredFirstInterest;
        } else if (proratedInterest !== null) {
          interest = proratedInterest;
        }

        if (firstInterestText || proratedInterest !== null) {
          principalPayment = Math.max(0, Math.min(balance, payment - interest - roundStampDuty));
        } else {
          const targetBalance = getRemainingBalanceForFixedPayment(
            monthlyPayment,
            monthlyRate,
            remainingRepaymentMonths
          );
          principalPayment = Math.max(0, Math.min(balance, balance - targetBalance));
          interest = Math.max(0, payment - principalPayment - roundStampDuty);
        }
        const remainingBalance = Math.max(0, balance - principalPayment);
        monthlyPayment = buildEqualPayment(remainingBalance, monthlyRate, remainingRepaymentMonths);
      } else {
        principalPayment =
          round === totalMonths ? balance : Math.min(balance, monthlyPayment - interest);
        payment = principalPayment + interest;
      }
      balance = Math.max(0, balance - principalPayment);
    }

    totalPayment += payment;
    totalInterest += interest;
    rows.push({
      round,
      payment,
      principal: principalPayment,
      interest,
      stampDuty: roundStampDuty,
      balance,
    });
  }

  return {
    rows,
    totalPayment,
    totalInterest,
    monthlyAverage: totalMonths ? totalPayment / totalMonths : 0,
  };
};

export default function CarInstallmentDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { carNumber: carNumberParam } = useParams();
  const state = location.state || {};
  const carNumber = state.carNumber || decodeURIComponent(carNumberParam || "");
  const installmentId = state.installmentId || state.sourceId || null;

  const [form, setForm] = useState({
    principal: state.principal ? String(state.principal) : "",
    annualRate: state.interestRate ? String(state.interestRate).replace(/%/g, "") : "",
    termMonths: state.termMonths ? String(state.termMonths) : "",
    graceMonths: state.graceMonths ? String(state.graceMonths) : "0",
    loanStartDate: state.loanStartDate || "",
    maturityDate: state.maturityDate || "",
    firstPaymentAmount: state.firstPaymentAmount ? String(state.firstPaymentAmount) : "",
    firstPaymentInterest: state.firstPaymentInterest ? String(state.firstPaymentInterest) : "",
    stampDuty: state.stampDuty ? String(state.stampDuty) : "",
  });
  const [scheduleOverrides, setScheduleOverrides] = useState(() =>
    normalizeScheduleOverrides(state.paymentScheduleOverrides)
  );
  const [saving, setSaving] = useState(false);

  const calculatedFirstPaymentInterest = useMemo(
    () =>
      calculateProratedInterest({
        principalAmount: parseNumber(form.principal),
        annualRate: parseNumber(form.annualRate),
        loanStartDate: form.loanStartDate,
        maturityDate: form.maturityDate,
      }),
    [form.annualRate, form.loanStartDate, form.maturityDate, form.principal]
  );

  useEffect(() => {
    if (calculatedFirstPaymentInterest === null) return;
    setForm((prev) => {
      const nextValue = String(calculatedFirstPaymentInterest);
      return String(prev.firstPaymentInterest ?? "") === nextValue
        ? prev
        : { ...prev, firstPaymentInterest: nextValue };
    });
  }, [calculatedFirstPaymentInterest]);

  const baseSchedule = useMemo(
    () =>
      buildSchedule({
        principal: form.principal,
        annualRate: form.annualRate,
        termMonths: form.termMonths,
        graceMonths: form.graceMonths,
        loanStartDate: form.loanStartDate,
        maturityDate: form.maturityDate,
        firstPaymentAmount: form.firstPaymentAmount,
        firstPaymentInterest: form.firstPaymentInterest,
        stampDuty: form.stampDuty,
      }),
    [
      form.annualRate,
      form.firstPaymentAmount,
      form.firstPaymentInterest,
      form.graceMonths,
      form.loanStartDate,
      form.maturityDate,
      form.principal,
      form.stampDuty,
      form.termMonths,
    ]
  );

  const schedule = useMemo(
    () => applyScheduleOverrides(baseSchedule.rows, scheduleOverrides, form.principal),
    [baseSchedule.rows, form.principal, scheduleOverrides]
  );

  const updateField = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: field === "principal" ? value.replace(/[^\d.-]/g, "") : value.replace(/[^\d.-]/g, ""),
    }));
  };

  const updateScheduleCell = (round, field, value) => {
    const cleanedValue = value.replace(/[^\d.-]/g, "");
    setScheduleOverrides((prev) => {
      const next = { ...prev };
      const current = { ...(next[round] || {}) };
      if (cleanedValue === "") {
        delete current[field];
      } else {
        current[field] = cleanedValue;
      }

      if (Object.keys(current).length === 0) {
        delete next[round];
      } else {
        next[round] = current;
      }
      return next;
    });
  };

  const renderScheduleInput = (row, field) => (
    <Input
      value={formatMoneyInput(row[field])}
      onChange={(event) => updateScheduleCell(row.round, field, event.target.value)}
      className="h-8 w-full text-right"
    />
  );

  const getRecurringPaymentAmount = () => {
    const paidRows = schedule.rows.filter((row) => row.round > 0 && parseNumber(row.payment) > 0);
    if (paidRows.length === 0) return null;
    const hasFirstPayment = parseNumber(form.firstPaymentAmount) > 0;
    const recurringRow = hasFirstPayment ? paidRows[1] || paidRows[0] : paidRows[0];
    return truncateNumber(parseNumber(recurringRow?.payment));
  };

  const handleSave = async () => {
    if (!installmentId) {
      showUserAlert("저장할 할부 내역 ID가 없습니다. 할부 내역 화면에서 저장한 뒤 다시 열어 주세요.");
      return;
    }

    const company = localStorage.getItem("email") || "";
    if (!company) {
      showUserAlert("회사 식별 정보가 없습니다. 다시 로그인해 주세요.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        balance_before_payment: truncateNumberOrNull(form.principal),
        interest_rate: form.annualRate ? String(form.annualRate) : null,
        maturity_round: truncateNumberOrNull(form.termMonths),
        grace_months: truncateNumberOrNull(form.graceMonths),
        loan_start_date: form.loanStartDate || null,
        maturity_date: form.maturityDate || null,
        first_payment_amount: truncateNumberOrNull(form.firstPaymentAmount),
        first_payment_interest: truncateNumberOrNull(form.firstPaymentInterest),
        stamp_duty: truncateNumberOrNull(form.stampDuty),
        amount: getRecurringPaymentAmount(),
        payment_schedule_overrides: serializeScheduleOverrides(scheduleOverrides),
      };

      const { data, error } = await supabase
        .from("car_installments")
        .update(payload)
        .eq("id", installmentId)
        .eq("company", company)
        .select("id")
        .single();

      if (error) throw error;
      if (!data) throw new Error("저장된 행을 확인하지 못했습니다.");

      showUserAlert("저장되었습니다.");
    } catch (error) {
      console.error("할부내역 상세 저장 실패:", error);
      showUserAlert("저장 실패: " + (error.message || JSON.stringify(error)));
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    const metaItems = [
      ["차량번호", carNumber || "-"],
      ["원금", formatMoneyInput(form.principal)],
      ["금리", form.annualRate ? `${form.annualRate}%` : ""],
      ["기간(개월)", form.termMonths],
      ["거치(개월)", form.graceMonths],
      ["대출일자", form.loanStartDate],
      ["만기일", form.maturityDate],
      ["첫달 금액", formatMoneyInput(form.firstPaymentAmount)],
      ["첫달 이자", formatMoneyInput(form.firstPaymentInterest)],
      ["인지대", formatMoneyInput(form.stampDuty)],
      ["총 상환금", formatMoney(schedule.totalPayment)],
      ["총 이자", formatMoney(schedule.totalInterest)],
      ["월 평균 원금+이자", formatMoney(schedule.monthlyAverage)],
    ];

    const metaHtml = metaItems
      .map(
        ([label, value]) => `
          <div class="box">
            <div class="label">${escapeHtml(label)}</div>
            <div class="value">${escapeHtml(value || "-")}</div>
          </div>
        `
      )
      .join("");

    const rowsHtml = schedule.rows
      .map(
        (row) => `
          <tr>
            <td class="center">${escapeHtml(row.round)}</td>
            <td class="right">${escapeHtml(formatMoney(row.payment))}</td>
            <td class="right">${escapeHtml(formatMoney(row.principal))}</td>
            <td class="right">${escapeHtml(formatMoney(row.interest))}</td>
            <td class="right">${escapeHtml(formatMoney(row.stampDuty))}</td>
            <td class="right">${escapeHtml(formatMoney(row.balance))}</td>
          </tr>
        `
      )
      .join("");

    openPrintWindow(
      "할부내역 상세",
      `
        <h1>할부내역 상세</h1>
        <div class="meta">${metaHtml}</div>
        <table>
          <thead>
            <tr>
              <th>회차</th>
              <th>상환금</th>
              <th>원금</th>
              <th>이자</th>
              <th>인지대</th>
              <th>잔액</th>
            </tr>
          </thead>
          <tbody>${rowsHtml}</tbody>
        </table>
      `
    );
  };

  return (
    <div className="space-y-1">
      <Header />
      <div className="px-4 pt-1 pb-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">할부내역 상세</h2>
          <Button variant="outline" onClick={() => navigate(-1)}>
            이전
          </Button>
        </div>

        <Card className="border-0 shadow-none">
          <CardContent className="bg-white p-0 space-y-3">
            <div className="text-sm text-gray-600">차량번호: {carNumber || "-"}</div>

            <div className="flex flex-wrap items-end gap-2">
              <label className="flex-none text-sm font-semibold">
                원금
                <Input
                  value={formatMoneyInput(form.principal)}
                  onChange={(event) => updateField("principal", event.target.value)}
                  className="mt-1 h-8 w-[112px] text-right"
                />
              </label>
              <label className="flex-none text-sm font-semibold">
                금리
                <Input
                  value={form.annualRate}
                  onChange={(event) => updateField("annualRate", event.target.value)}
                  className="mt-1 h-8 w-[64px] text-right"
                  placeholder="%"
                />
              </label>
              <label className="flex-none text-sm font-semibold">
                기간(개월)
                <Input
                  value={form.termMonths}
                  onChange={(event) => updateField("termMonths", event.target.value)}
                  className="mt-1 h-8 w-[64px] text-right"
                />
              </label>
              <label className="flex-none text-sm font-semibold">
                거치(개월)
                <Input
                  value={form.graceMonths}
                  onChange={(event) => updateField("graceMonths", event.target.value)}
                  className="mt-1 h-8 w-[64px] text-right"
                />
              </label>
              <label className="flex-none text-sm font-semibold">
                대출일자
                <Input
                  type="date"
                  value={form.loanStartDate}
                  onChange={(event) => updateField("loanStartDate", event.target.value)}
                  className="mt-1 h-8 w-[126px]"
                />
              </label>
              <label className="flex-none text-sm font-semibold">
                만기일
                <Input
                  type="date"
                  value={form.maturityDate}
                  onChange={(event) => updateField("maturityDate", event.target.value)}
                  className="mt-1 h-8 w-[126px]"
                />
              </label>
              <label className="flex-none text-sm font-semibold">
                첫달 금액
                <Input
                  value={formatMoneyInput(form.firstPaymentAmount)}
                  onChange={(event) => updateField("firstPaymentAmount", event.target.value)}
                  className="mt-1 h-8 w-[112px] text-right"
                />
              </label>
              <label className="flex-none text-sm font-semibold">
                첫달 이자
                <Input
                  value={formatMoneyInput(form.firstPaymentInterest)}
                  onChange={(event) => updateField("firstPaymentInterest", event.target.value)}
                  className="mt-1 h-8 w-[96px] text-right"
                />
              </label>
              <label className="flex-none text-sm font-semibold">
                인지대
                <Input
                  value={formatMoneyInput(form.stampDuty)}
                  onChange={(event) => updateField("stampDuty", event.target.value)}
                  className="mt-1 h-8 w-[84px] text-right"
                />
              </label>
            </div>

            <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
              <div className="border bg-gray-50 px-3 py-2 text-sm">
                <div className="text-gray-600">총 상환금</div>
                <div className="text-right font-semibold">{formatMoney(schedule.totalPayment)}</div>
              </div>
              <div className="border bg-gray-50 px-3 py-2 text-sm">
                <div className="text-gray-600">총 이자</div>
                <div className="text-right font-semibold">{formatMoney(schedule.totalInterest)}</div>
              </div>
              <div className="border bg-gray-50 px-3 py-2 text-sm">
                <div className="text-gray-600">월 평균 원금+이자</div>
                <div className="text-right font-semibold">{formatMoney(schedule.monthlyAverage)}</div>
              </div>
            </div>

            <div className="max-h-[68vh] overflow-auto">
              <table className="table-fixed w-full border border-gray-300 text-sm whitespace-nowrap">
                <colgroup>
                  <col className="w-16" />
                  <col className="w-32" />
                  <col className="w-32" />
                  <col className="w-32" />
                  <col className="w-28" />
                  <col className="w-32" />
                </colgroup>
                <thead className="sticky top-0 z-20 bg-blue-500 text-white">
                  <tr>
                    <th className="border px-2 py-1 text-center">회차</th>
                    <th className="border px-2 py-1 text-center">상환금</th>
                    <th className="border px-2 py-1 text-center">원금</th>
                    <th className="border px-2 py-1 text-center">이자</th>
                    <th className="border px-2 py-1 text-center">인지대</th>
                    <th className="border px-2 py-1 text-center">잔액</th>
                  </tr>
                </thead>
                <tbody>
                  {schedule.rows.map((row) => (
                    <tr key={row.round} className="hover:bg-gray-50">
                      <td className="border px-2 py-1 text-center">{row.round}</td>
                      <td className="border px-1 py-1 text-right">{renderScheduleInput(row, "payment")}</td>
                      <td className="border px-1 py-1 text-right">{renderScheduleInput(row, "principal")}</td>
                      <td className="border px-1 py-1 text-right">{renderScheduleInput(row, "interest")}</td>
                      <td className="border px-1 py-1 text-right">{renderScheduleInput(row, "stampDuty")}</td>
                      <td className="border px-1 py-1 text-right">{renderScheduleInput(row, "balance")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={handlePrint}>
                인쇄
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? "저장중..." : "저장"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
